import { useState, useEffect } from "react";
import { authFetch } from "../api";
import Header from "../components/Header";

const ACTION_LABELS = {
  listing_edit: { label: "Listing Edit", bg: "#e3f2fd", color: "#1565c0" },
  listing_verified: { label: "Listing Verified", bg: "#e8eaf6", color: "#283593" },
  property_edit: { label: "Building Edit", bg: "#e8f5e9", color: "#2e7d32" },
  property_verified: { label: "Building Verified", bg: "#fff3e0", color: "#e65100" },
  se_listing: { label: "SE Listing", bg: "#f3e5f5", color: "#7b1fa2" },
  se_property: { label: "SE Building", bg: "#fce4ec", color: "#c62828" },
};

export default function AuditPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    authFetch(`/audit/activity?days=${days}&page=${page}&per_page=50`)
      .then(setData)
      .catch((err) => console.error("Error fetching audit data:", err))
      .finally(() => setLoading(false));
  }, [days, page]);

  // Reset page when days changes
  const handleDaysChange = (e) => {
    setPage(1);
    setDays(Number(e.target.value));
  };

  // Build daily summary across all activity types
  const buildDailySummary = () => {
    if (!data) return [];
    const byDay = {};
    const addEntries = (rows, category) => {
      for (const r of rows) {
        if (!byDay[r.day]) byDay[r.day] = {};
        if (!byDay[r.day][r.user_name]) byDay[r.day][r.user_name] = {};
        byDay[r.day][r.user_name][category] = (byDay[r.day][r.user_name][category] || 0) + r.count;
      }
    };
    addEntries(data.listing_edits, "listing_edits");
    addEntries(data.listing_verifications || [], "listing_verified");
    addEntries(data.property_edits, "property_edits");
    addEntries(data.property_verifications, "property_verified");
    addEntries(data.se_listings, "se_listings");
    addEntries(data.se_properties, "se_properties");
    return Object.entries(byDay).sort((a, b) => b[0].localeCompare(a[0]));
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      <Header showLogout />
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 24 }}>Audit Log</h1>
          <select
            value={days}
            onChange={handleDaysChange}
            style={{ padding: "6px 12px", fontSize: 14, borderRadius: 4, border: "1px solid #ccc" }}
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
        </div>

        {loading && <p>Loading...</p>}

        {data && !loading && (
          <>
            {/* Daily Summary */}
            <h2 style={{ fontSize: 18, marginBottom: 12 }}>Daily Summary</h2>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 32, background: "#fff", borderRadius: 8, overflow: "hidden" }}>
              <thead>
                <tr style={{ background: "#f0f0f0", textAlign: "left" }}>
                  <th style={{ padding: "10px 12px" }}>Date</th>
                  <th style={{ padding: "10px 12px" }}>User</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>Listing Edits</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>Listings Verified</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>Building Edits</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>Buildings Verified</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>SE Listings</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>SE Buildings</th>
                </tr>
              </thead>
              <tbody>
                {buildDailySummary().map(([day, users]) => {
                  const userEntries = Object.entries(users);
                  return userEntries.map(([user, counts], i) => (
                    <tr key={`${day}-${user}`} style={{ borderTop: i === 0 ? "2px solid #e0e0e0" : "1px solid #f0f0f0" }}>
                      <td style={{ padding: "8px 12px", fontWeight: i === 0 ? 600 : 400 }}>{i === 0 ? day : ""}</td>
                      <td style={{ padding: "8px 12px" }}>{user}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right" }}>{counts.listing_edits || 0}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right" }}>{counts.listing_verified || 0}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right" }}>{counts.property_edits || 0}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right" }}>{counts.property_verified || 0}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right" }}>{counts.se_listings || 0}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right" }}>{counts.se_properties || 0}</td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>

            {/* Recent Updates */}
            <h2 style={{ fontSize: 18, marginBottom: 12 }}>Recent Updates</h2>
            <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 8, overflow: "hidden" }}>
              <thead>
                <tr style={{ background: "#f0f0f0", textAlign: "left" }}>
                  <th style={{ padding: "10px 12px" }}>When</th>
                  <th style={{ padding: "10px 12px" }}>User</th>
                  <th style={{ padding: "10px 12px" }}>Action</th>
                  <th style={{ padding: "10px 12px" }}>Address</th>
                  <th style={{ padding: "10px 12px" }}>Unit</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_updates.map((r, i) => {
                  const actionInfo = ACTION_LABELS[r.action] || { label: r.action, bg: "#eee", color: "#333" };
                  return (
                    <tr key={i} style={{ borderTop: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "8px 12px", fontSize: 13, whiteSpace: "nowrap" }}>{formatTime(r.action_at)}</td>
                      <td style={{ padding: "8px 12px" }}>{r.user_name}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{
                          display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 12,
                          background: actionInfo.bg, color: actionInfo.color,
                        }}>
                          {actionInfo.label}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px" }}>{r.address}</td>
                      <td style={{ padding: "8px 12px" }}>{r.unit_number || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {data.recent_total > 0 && (() => {
              const totalPages = Math.ceil(data.recent_total / (data.recent_per_page || 50));
              return (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginTop: 12 }}>
                  <button
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page <= 1}
                    style={{ padding: "6px 16px", fontSize: 14, borderRadius: 4, border: "1px solid #ccc", background: page <= 1 ? "#f0f0f0" : "#fff", cursor: page <= 1 ? "default" : "pointer" }}
                  >
                    ← Prev
                  </button>
                  <span style={{ fontSize: 14, color: "#666" }}>
                    Page {page} of {totalPages} ({data.recent_total} total)
                  </span>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages}
                    style={{ padding: "6px 16px", fontSize: 14, borderRadius: 4, border: "1px solid #ccc", background: page >= totalPages ? "#f0f0f0" : "#fff", cursor: page >= totalPages ? "default" : "pointer" }}
                  >
                    Next →
                  </button>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}
